import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getAllUsers } from '../services/userService';

const ChatPanel = ({ socket, currentUser, onClose, dmUser = null, token }) => {
  const [activeChat, setActiveChat] = useState(dmUser); // {id, name} = DM user
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [showConversations, setShowConversations] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserPicker, setShowUserPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const audioContextRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('chatSoundEnabled');
    return saved === null ? true : saved === 'true';
  });

  // Reuse a single AudioContext
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch {
        return null;
      }
    }
    return audioContextRef.current;
  }, []);

  // Create notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;

    const audioContext = getAudioContext();
    if (!audioContext) return;

    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch {
      // AudioContext may be suspended if user hasn't interacted
    }
  }, [soundEnabled, getAudioContext]);

  // Toggle sound setting
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('chatSoundEnabled', newValue.toString());
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load all users for the user picker
  useEffect(() => {
    if (token) {
      getAllUsers(token)
        .then((data) => setAllUsers(data))
        .catch((err) => console.error('Failed to load users for chat picker', err));
    }
  }, [token]);

  // Update activeChat when dmUser prop changes
  useEffect(() => {
    setActiveChat(dmUser);
  }, [dmUser]);

  // Request chat history and conversations when chat changes
  useEffect(() => {
    if (socket) {
      socket.emit('getChatHistory', { recipientId: activeChat?.id || null });
      socket.emit('getConversations');
      // Mark messages from the active chat partner as read
      if (activeChat?.id) {
        socket.emit('markChatRead', { recipientId: activeChat.id });
      }
    }
  }, [socket, activeChat]);

  // Listen for new messages, history, and conversations
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (msg) => {
      const isDmForMe = msg.recipientId === currentUser?.id || msg.senderId === currentUser?.id;
      const isFromOtherUser = msg.senderId !== currentUser?.id;

      if (activeChat && isDmForMe &&
          (msg.senderId === activeChat.id || msg.recipientId === activeChat.id)) {
        setMessages(prev => [...prev, msg]);
        if (isFromOtherUser) {
          playNotificationSound();
          // Mark as read since we're viewing this conversation
          socket.emit('markChatRead', { recipientId: msg.senderId });
        }
      }
      socket.emit('getConversations');
    };

    const handleHistory = (data) => {
      const expectedId = activeChat?.id || null;
      if (data.recipientId === expectedId) {
        setMessages(data.messages || []);
        // Mark messages as read after loading history
        if (activeChat?.id) {
          socket.emit('markChatRead', { recipientId: activeChat.id });
        }
      }
    };

    const handleConversations = (data) => {
      setConversations(data || []);
    };

    socket.on('newChatMessage', handleNewMessage);
    socket.on('chatHistory', handleHistory);
    socket.on('conversationsList', handleConversations);

    return () => {
      socket.off('newChatMessage', handleNewMessage);
      socket.off('chatHistory', handleHistory);
      socket.off('conversationsList', handleConversations);
    };
  }, [socket, activeChat, currentUser, playNotificationSound]);

  const handleSend = () => {
    if (!newMessage.trim() || !socket) return;

    socket.emit('sendChatMessage', {
      message: newMessage.trim(),
      recipientId: activeChat?.id || null
    });

    setNewMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
  };

  const handleDeleteConversation = (e, recipientId, recipientName) => {
    e.stopPropagation();
    if (!socket) return;
    if (!window.confirm(`Slette samtalen med ${recipientName}? Alle meldinger vil bli slettet.`)) return;
    socket.emit('deleteConversation', { recipientId });
    if (activeChat?.id === recipientId) {
      setActiveChat(null);
      setMessages([]);
    }
  };

  const startChatWithUser = (user) => {
    setActiveChat({ id: user.id, name: `${user.firstName} ${user.lastName}` });
    setShowUserPicker(false);
    setShowConversations(false);
    setUserSearch('');
  };

  // Filter users for the picker (exclude self)
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return allUsers
      .filter((u) => u.id !== currentUser?.id)
      .filter((u) => !q || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [allUsers, userSearch, currentUser]);

  // Total unread count across all conversations
  const totalUnread = useMemo(() => {
    return conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
  }, [conversations]);

  return (
    <div style={{
      position: 'fixed',
      bottom: '60px',
      right: '20px',
      width: '380px',
      height: '500px',
      background: 'white',
      borderRadius: '8px',
      boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1100,
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
        color: 'white',
        padding: '0.75rem 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => { setShowConversations(!showConversations); setShowUserPicker(false); }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '28px',
              height: '28px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
            title="Mine samtaler"
          >
            ☰
          </button>
          <span style={{ fontWeight: '600' }}>
            💬 {activeChat ? activeChat.name : 'Velg en bruker'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => { setShowUserPicker(!showUserPicker); setShowConversations(false); }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '28px',
              height: '28px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
            title="Ny samtale"
          >
            ✎
          </button>
          <button
            onClick={toggleSound}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '28px',
              height: '28px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
            title={soundEnabled ? 'Slå av lyd' : 'Slå på lyd'}
          >
            {soundEnabled ? '🔔' : '🔕'}
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* User picker (new conversation) */}
      {showUserPicker && (
        <div style={{
          position: 'absolute',
          top: '52px',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'white',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', fontWeight: '600', color: '#374151' }}>
            Ny samtale
          </div>
          <div style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #f3f4f6' }}>
            <input
              type="text"
              autoFocus
              placeholder="Søk etter bruker …"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                fontSize: '0.85rem',
              }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredUsers.length === 0 ? (
              <div style={{ padding: '1rem', color: '#94a3b8', textAlign: 'center', fontSize: '0.85rem' }}>
                {allUsers.length === 0 ? 'Laster brukere …' : 'Ingen brukere funnet.'}
              </div>
            ) : (
              filteredUsers.map((u) => (
                <div
                  key={u.id}
                  onClick={() => startChatWithUser(u)}
                  style={{
                    padding: '0.6rem 1rem',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <span style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    flexShrink: 0,
                  }}>
                    {u.firstName?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '500', color: '#111827', fontSize: '0.85rem' }}>
                      {u.firstName} {u.lastName}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{u.email}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Conversations sidebar */}
      {showConversations && !showUserPicker && (
        <div style={{
          position: 'absolute',
          top: '52px',
          left: 0,
          right: 0,
          bottom: 0,
          background: 'white',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0', fontWeight: '600', color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Mine samtaler
            {totalUnread > 0 && (
              <span style={{ background: '#ef4444', color: 'white', fontSize: '0.7rem', fontWeight: '700', borderRadius: '10px', padding: '0.1rem 0.5rem' }}>
                {totalUnread} ulest
              </span>
            )}
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {/* DM conversations only */}
            {conversations.length === 0 ? (
              <div style={{ padding: '1rem', color: '#94a3b8', textAlign: 'center', fontSize: '0.85rem' }}>
                Ingen samtaler ennå. Klikk ✎ for å starte en ny.
              </div>
            ) : (
              conversations.map((conv, idx) => (
                <div
                  key={conv.otherId || idx}
                  onClick={() => { 
                    setActiveChat({ id: conv.otherId, name: conv.name }); 
                    setShowConversations(false); 
                  }}
                  style={{
                    padding: '0.75rem 1rem',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    background: activeChat?.id === conv.otherId ? '#eff6ff' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <span style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {conv.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', color: '#111827', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      {conv.name}
                      {conv.unreadCount > 0 && (
                        <span style={{ background: '#ef4444', color: 'white', fontSize: '0.65rem', fontWeight: '700', borderRadius: '10px', padding: '0.05rem 0.4rem', minWidth: '16px', textAlign: 'center' }}>
                          {conv.unreadCount}
                        </span>
                      )}
                    </div>
                    <div style={{ 
                      color: '#64748b', 
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {conv.lastMessage || 'Ingen meldinger'}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteConversation(e, conv.otherId, conv.name)}
                    title="Slett samtale"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      padding: '0.25rem',
                      borderRadius: '8px',
                      flexShrink: 0
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fee2e2'; }}
                    onMouseOut={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    🗑️
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '0.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        background: '#f3f4f6'
      }}>
        {messages.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#94a3b8',
            marginTop: '2rem'
          }}>
            {activeChat ? 'Ingen meldinger ennå' : 'Velg en bruker for å starte samtale'}
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOwn = msg.senderId === currentUser?.id;
            return (
              <div
                key={msg.id || idx}
                style={{
                  alignSelf: isOwn ? 'flex-end' : 'flex-start',
                  maxWidth: '80%'
                }}
              >
                {!isOwn && (
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#64748b',
                    marginBottom: '2px',
                    marginLeft: '8px'
                  }}>
                    {msg.senderName}
                  </div>
                )}
                <div style={{
                  background: isOwn ? '#3b82f6' : 'white',
                  color: isOwn ? 'white' : '#111827',
                  padding: '0.5rem 0.75rem',
                  borderRadius: isOwn ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  {msg.message}
                </div>
                <div style={{
                  fontSize: '0.65rem',
                  color: '#94a3b8',
                  marginTop: '2px',
                  textAlign: isOwn ? 'right' : 'left',
                  marginRight: isOwn ? '8px' : '0',
                  marginLeft: isOwn ? '0' : '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  justifyContent: isOwn ? 'flex-end' : 'flex-start',
                }}>
                  {formatTime(msg.createdAt)}
                  {isOwn && msg.isRead && (
                    <span style={{ color: '#3b82f6', fontSize: '0.6rem' }} title="Lest">✓✓</span>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '0.75rem',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        gap: '0.5rem',
        background: 'white'
      }}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={activeChat ? `Melding til ${activeChat.name}...` : 'Velg en bruker først...'}
          style={{
            flex: 1,
            padding: '0.6rem 0.75rem',
            border: '1px solid #d1d5db',
            borderRadius: '20px',
            outline: 'none',
            fontSize: '0.9rem'
          }}
        />
        <button
          onClick={handleSend}
          disabled={!newMessage.trim() || !activeChat}
          style={{
            background: (newMessage.trim() && activeChat) ? '#3b82f6' : '#e2e8f0',
            color: (newMessage.trim() && activeChat) ? 'white' : '#94a3b8',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            cursor: (newMessage.trim() && activeChat) ? 'pointer' : 'default',
            fontSize: '1.1rem'
          }}
        >
          ➤
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
